// A proposal read from a Word file (noc-strapi utils/proposalReading), made into the dashboard's
// proposal: the text and figures come from the file; the company details, footer and section
// headings come from the dashboard's own template.
import { createProposalTemplateContent, normalizeProposalContent } from '@/utils/proposalDocument';

const comparable = (value) => String(value ?? '').toLowerCase().replace(/\s+/g, ' ').trim();

/** The catalog entry with this name, ignoring case and spacing. */
export const findCatalogOption = (options = [], name = '') => {
	const wanted = comparable(name);
	if (!wanted) return null;
	return options.find((option) => comparable(option?.name) === wanted) || null;
};

const list = (value) => (Array.isArray(value) ? value : []);
const figure = (value, fallback) => (typeof value === 'number' && Number.isFinite(value) ? value : fallback);

/**
 * @param {object} options
 * @param {object} options.fields what the server read from the file
 * @param {string} [options.footerText] the footer new proposals use
 * @param {Array<{id, name}>} [options.clientOptions] the Clients catalog
 * @param {Array<{id, name}>} [options.buildingOptions] the Buildings catalog
 */
export const buildImportedProposalContent = ({
	fields = {},
	footerText = '',
	clientOptions = [],
	buildingOptions = [],
} = {}) => {
	const template = createProposalTemplateContent();
	const client = findCatalogOption(clientOptions, fields.clientName);
	const pricing = fields.pricing || {};
	const content = {
		...template,
		subtitle: fields.subtitle ?? '',
		reference: fields.reference ?? '',
		proposalDate: fields.proposalDate ?? '',
		preparedBy: fields.preparedBy || template.preparedBy,
		clientId: client?.id ?? null,
		clientName: client?.name || fields.clientName || '',
		properties: list(fields.properties).map((property) => ({
			buildingId: findCatalogOption(buildingOptions, property?.name)?.id ?? null,
			name: property?.name || '',
			address: property?.address || '',
		})),
		summaryParagraphs: list(fields.summaryParagraphs),
		serviceAreas: list(fields.serviceAreas),
		taskSections: list(fields.taskSections),
		scopeClarification: {
			...template.scopeClarification,
			title: fields.scopeClarification?.title || template.scopeClarification.title,
			paragraphs: list(fields.scopeClarification?.paragraphs),
		},
		schedule: {
			...template.schedule,
			intro: fields.schedule?.intro ?? '',
			rows: list(fields.schedule?.rows),
		},
		// Figures the file does not give stay at nothing rather than the template's examples; only
		// weeks per year and UK VAT keep their usual values.
		pricing: {
			hourlyRate: figure(pricing.hourlyRate, 0),
			hoursPerVisit: figure(pricing.hoursPerVisit, 0),
			visitsPerWeekPerBlock: figure(pricing.visitsPerWeekPerBlock, 0),
			blockCount: figure(pricing.blockCount, 0),
			weeksPerYear: figure(pricing.weeksPerYear, template.pricing.weeksPerYear),
			vatRate: figure(pricing.vatRate, template.pricing.vatRate),
			notes: list(pricing.notes),
		},
		textSections: list(fields.textSections).map((section, index) => ({
			id: `imported-${index + 1}`,
			title: section?.title || '',
			paragraphs: list(section?.paragraphs),
		})),
		acceptance: {
			...template.acceptance,
			paragraphs: list(fields.acceptance?.paragraphs),
			signatories: list(fields.acceptance?.signatories),
		},
	};
	if (footerText) content.footerText = footerText;
	return normalizeProposalContent(content);
};

/** What the office should look at before keeping the proposal: the dashboard's checks, then Claude's. */
export const proposalImportNotes = ({ fields = {}, content = {}, warnings = [] } = {}) => {
	const notes = [];
	if (!content.reference) notes.push('The file has no reference. Add one in Details.');
	if (!content.proposalDate) notes.push('The file has no date. Add it in Details.');
	if (!fields.clientName) notes.push('No client was found in the file.');
	else if (!content.clientId) notes.push(`${fields.clientName} is not in the Clients catalog, so only the name is kept.`);
	const unknownBuildings = list(content.properties)
		.filter((property) => property.name && !property.buildingId)
		.map((property) => property.name);
	if (unknownBuildings.length) notes.push(`Not in the Buildings catalog: ${unknownBuildings.join(', ')}.`);
	const priced = list(content.schedule?.rows).some((row) => row.block && row.hourlyRate) || Boolean(fields.pricing?.hourlyRate);
	if (!priced) notes.push('No hourly rate was found. Fill in Pricing if the proposal needs one.');
	return [...notes, ...list(warnings)];
};
